import {createSlice} from "@reduxjs/toolkit";
import {LOGOUT} from "store/mapXActionTypes";
import * as thunks from "store/extended-search/extendedSearchAsyncActions";
import type {TExtendedSearchError, TExtendedSearchReducerState} from "store/extended-search/types";
import {
	foundUrlsKeyOf,
	isNewestRequest,
	remainingAfterApplying,
	requestDispatchedFor,
	withoutRequest,
} from "store/extended-search/requestOrdering";

export const extendedSearchInitialState: TExtendedSearchReducerState = {
	runs: [],
	currentRun: null,
	foundUrls: [],
	foundUrlsPagination: null,
	currentRunRequestIds: [],
	requestedRunId: null,
	foundUrlsRequestIds: [],
	foundUrlsQueryKey: null,
	limits: null,
	createdRun: null,
	isLoading: false,
	isRunLoading: false,
	isFoundUrlsLoading: false,
	isCreating: false,
	isImporting: false,
	isAborting: false,
	error: null,
	importError: null,
	importMessage: null,
	abortError: null,
};

/**
 * Every thunk rejects with the server's own message via `rejectWithValue`. `action.payload` is
 * absent only when the thunk itself threw (a bug, not a refusal), so the fallback is a last resort.
 */
const rejectionOf = (
	payload: TExtendedSearchError | undefined,
	errorMessage: string | undefined,
	fallbackMessage: string,
): TExtendedSearchError => payload ?? {status: null, message: errorMessage || fallbackMessage};

const extendedSearchSlice = createSlice({
	name: "extendedSearch",
	initialState: extendedSearchInitialState,
	reducers: {
		errorCleared: (state) => {
			state.error = null;
		},
		// The import banner is dismissed on its own, separately from the shared error.
		importFeedbackCleared: (state) => {
			state.importError = null;
			state.importMessage = null;
		},
		abortErrorCleared: (state) => {
			state.abortError = null;
		},
		createdRunCleared: (state) => {
			state.createdRun = null;
		},
		currentRunCleared: (state) => {
			state.currentRun = null;
			state.foundUrls = [];
			state.foundUrlsPagination = null;
			// Nothing is being asked for any more, so a response still in flight belongs to a run
			// the operator has already closed and must not reopen it. An empty queue matches
			// nothing, which is exactly that.
			state.currentRunRequestIds = [];
			state.requestedRunId = null;
			state.foundUrlsRequestIds = [];
			state.foundUrlsQueryKey = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(thunks.fetchExtendedSearchLimits.fulfilled, (state, action) => {
				state.limits = action.payload;
			})
			.addCase(thunks.fetchExtendedSearchLimits.rejected, (state, action) => {
				state.error = rejectionOf(
					action.payload,
					action.error?.message,
					"Could not load the Extended Search limits",
				).message;
			})
			.addCase(thunks.fetchExtendedSearchRuns.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(thunks.fetchExtendedSearchRuns.fulfilled, (state, action) => {
				state.isLoading = false;
				state.runs = action.payload;
			})
			.addCase(thunks.fetchExtendedSearchRuns.rejected, (state, action) => {
				state.isLoading = false;
				state.error = rejectionOf(
					action.payload,
					action.error?.message,
					"Could not load the Extended Search runs",
				).message;
			})
			.addCase(thunks.fetchExtendedSearchRun.pending, (state, action) => {
				state.isRunLoading = true;
				state.currentRunRequestIds = requestDispatchedFor(
					state.currentRunRequestIds,
					action.meta.requestId,
					state.requestedRunId,
					action.meta.arg,
				);
				state.requestedRunId = action.meta.arg;
			})
			.addCase(thunks.fetchExtendedSearchRun.fulfilled, (state, action) => {
				// Open run A, then run B before A answers: A's late fulfilment used to replace B's
				// detail, RunTable then rejected the mismatched id, and because the selection never
				// changed again nothing refetched — B's row sat on "This run has no detail yet".
				//
				// Matched on the request rather than on the run id, so two overlapping requests for
				// the *same* run — the poll and the post-import refresh — cannot both pass and let
				// the older snapshot land last.
				const remaining = remainingAfterApplying(
					state.currentRunRequestIds,
					action.meta.requestId,
				);

				if (remaining === null) return;

				state.currentRunRequestIds = remaining;
				state.isRunLoading = false;
				// Stored whole rather than mapped onto a narrower shape, so `error_message` — the
				// only place a run explains why it lost the import gate — survives into the store.
				state.currentRun = action.payload;
			})
			.addCase(thunks.fetchExtendedSearchRun.rejected, (state, action) => {
				// A failure belonging to a request that has already been superseded says nothing
				// about the one in flight, and clearing the flag here would claim that request had
				// finished. Unlike a fulfilment, this is gated on being the *newest* request.
				if (!isNewestRequest(state.currentRunRequestIds, action.meta.requestId)) return;

				state.currentRunRequestIds = withoutRequest(
					state.currentRunRequestIds,
					action.meta.requestId,
				);
				state.isRunLoading = false;
				state.error = rejectionOf(
					action.payload,
					action.error?.message,
					"Could not load this Extended Search run",
				).message;
			})
			.addCase(thunks.fetchExtendedSearchFoundUrls.pending, (state, action) => {
				state.isFoundUrlsLoading = true;

				const queryKey = foundUrlsKeyOf(action.meta.arg);

				state.foundUrlsRequestIds = requestDispatchedFor(
					state.foundUrlsRequestIds,
					action.meta.requestId,
					state.foundUrlsQueryKey,
					queryKey,
				);
				state.foundUrlsQueryKey = queryKey;
				// Dropped, not kept: the next page — or the next run — must never be read through
				// rows belonging to the previous request.
				state.foundUrls = [];
				state.foundUrlsPagination = null;
			})
			.addCase(thunks.fetchExtendedSearchFoundUrls.fulfilled, (state, action) => {
				// Change the filter while the previous page is still in flight and the older answer
				// can land last, replacing both rows and pagination while the control shows the
				// newer filter — the operator then reviews the wrong set of URLs.
				//
				// The same panel also refetches on its own as statuses and counters change, so the
				// two racing requests are often for an identical run, page and filter. Only the
				// request id tells those apart.
				const remaining = remainingAfterApplying(
					state.foundUrlsRequestIds,
					action.meta.requestId,
				);

				if (remaining === null) return;

				state.foundUrlsRequestIds = remaining;
				state.isFoundUrlsLoading = false;
				state.foundUrls = action.payload.results ?? [];
				state.foundUrlsPagination = action.payload.pagination ?? null;
			})
			.addCase(thunks.fetchExtendedSearchFoundUrls.rejected, (state, action) => {
				if (!isNewestRequest(state.foundUrlsRequestIds, action.meta.requestId)) return;

				state.foundUrlsRequestIds = withoutRequest(
					state.foundUrlsRequestIds,
					action.meta.requestId,
				);
				state.isFoundUrlsLoading = false;
				state.error = rejectionOf(
					action.payload,
					action.error?.message,
					"Could not load the discovered profile URLs",
				).message;
			})
			.addCase(thunks.createExtendedSearchRun.pending, (state) => {
				state.isCreating = true;
				state.error = null;
				state.createdRun = null;
			})
			.addCase(thunks.createExtendedSearchRun.fulfilled, (state, action) => {
				state.isCreating = false;
				state.createdRun = action.payload;
			})
			.addCase(thunks.createExtendedSearchRun.rejected, (state, action) => {
				state.isCreating = false;
				// The server's size-limit refusal, word for word.
				state.error = rejectionOf(
					action.payload,
					action.error?.message,
					"Could not start the Extended Search run",
				).message;
			})
			.addCase(thunks.startExtendedSearchRunImport.pending, (state) => {
				state.isImporting = true;
				state.importError = null;
				state.importMessage = null;
			})
			.addCase(thunks.startExtendedSearchRunImport.fulfilled, (state, action) => {
				state.isImporting = false;
				state.importMessage = action.payload.message;
			})
			.addCase(thunks.startExtendedSearchRunImport.rejected, (state, action) => {
				state.isImporting = false;
				// Kept in its own slot, not folded into `error`: a 409 names the run that holds the
				// import gate and the operator has to be able to read it.
				state.importError = rejectionOf(
					action.payload,
					action.error?.message,
					"Could not schedule the import",
				);
			})
			.addCase(thunks.abortExtendedSearchRun.pending, (state) => {
				state.isAborting = true;
				state.abortError = null;
			})
			.addCase(thunks.abortExtendedSearchRun.fulfilled, (state) => {
				// Nothing is read from the body: the refetch the thunk dispatched is what tells
				// the page the run is Cancelled.
				state.isAborting = false;
			})
			.addCase(thunks.abortExtendedSearchRun.rejected, (state, action) => {
				state.isAborting = false;
				// Its own slot, like the import refusal: a 409 here is the server explaining that
				// the money is already committed, and that has to survive to the operator.
				state.abortError = rejectionOf(
					action.payload,
					action.error?.message,
					"Could not abort this Extended Search run",
				);
			})
			.addCase(LOGOUT, () => ({...extendedSearchInitialState}));
	},
});

export const {
	errorCleared,
	importFeedbackCleared,
	abortErrorCleared,
	createdRunCleared,
	currentRunCleared,
} = extendedSearchSlice.actions;

export default extendedSearchSlice.reducer;
